import { ApiError } from "@aurumiq/api-client";
import { Button } from "@aurumiq/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@aurumiq/ui/components/card";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { PageHeader } from "@/app/page-header";
import { usePermission } from "@/auth/use-permission";
import { SelectField, TextAreaField, TextField } from "@/components/field";
import { QueryState } from "@/components/query-state";
import { errorMessage, rawApi } from "@/lib/api";

const statuses = {
  not_started: "Not started",
  in_progress: "In progress",
  blocked: "Blocked",
  verified: "Verified",
};
type Status = keyof typeof statuses;
export type ReadinessTask = {
  id: string;
  category: string;
  title: string;
  description: string;
  status: Status;
  owner: string;
  due_date: string | null;
  blocker: string;
  evidence: string;
  notes: string;
  revision: number;
  updated_at: string | null;
  updated_by: string | null;
};
const queryKey = ["admin", "fta-readiness"];
const endpoint = "/api/v1/admin/readiness";

export function FtaReadinessPage() {
  const query = useQuery({ queryKey, queryFn: () => rawApi.get<ReadinessTask[]>(endpoint) });
  return (
    <>
      <PageHeader
        title="FTA readiness"
        description="Platform implementation, provider onboarding and evidence for UAE e-invoicing readiness."
      />
      <QueryState query={query}>{(tasks) => <Tracker tasks={tasks} />}</QueryState>
    </>
  );
}

function Tracker({ tasks }: { tasks: ReadinessTask[] }) {
  const canEdit = usePermission("readiness:write");
  const canAudit = usePermission("audit:read");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [saved, setSaved] = useState("");
  const complete = tasks.filter((task) => task.status === "verified").length;
  const today = new Date().toLocaleDateString("en-CA");
  const overdue = (task: ReadinessTask) =>
    task.status !== "verified" && !!task.due_date && task.due_date < today;
  const visible = tasks.filter(
    (task) =>
      (filter === "all" || task.status === filter || (filter === "overdue" && overdue(task))) &&
      `${task.title} ${task.category} ${task.owner} ${task.blocker}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm">
        <p className="font-semibold">Readiness evidence does not establish FTA approval.</p>
        <p className="mt-1 text-muted-foreground">
          This shared checklist records reviewed work and dependencies. Provider acceptance and
          compliance sign-off require their own evidence. Initial records reflect the 6 October 2026
          review; statuses are maintained by staff, not live system checks.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {[
          ["Verified tasks", `${complete} / ${tasks.length}`],
          ["Blocked", tasks.filter((task) => task.status === "blocked").length],
          ["Overdue", tasks.filter(overdue).length],
        ].map(([label, value]) => (
          <Card key={label}>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">{label}</p>
              <p className="mt-1 text-2xl font-semibold">{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <div>
        <p className="mb-2 text-sm">
          Checklist completion · {Math.round((complete / tasks.length) * 100)}%
        </p>
        <progress
          className="h-2 w-full accent-primary"
          aria-label="Checklist completion"
          value={complete}
          max={tasks.length}
        />
      </div>
      <div className="grid items-end gap-3 sm:grid-cols-[1fr_220px_auto]">
        <TextField
          label="Search tasks"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Task, owner or dependency"
        />
        <SelectField
          label="Status filter"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        >
          <option value="all">All tasks</option>
          {Object.entries(statuses).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
          <option value="overdue">Overdue</option>
        </SelectField>
        {canAudit && (
          <Link className="pb-2 text-sm underline" to="/settings/audit">
            View audit log
          </Link>
        )}
      </div>
      {saved && (
        <p role="status" className="text-sm">
          {saved}
        </p>
      )}
      {!canEdit && (
        <p className="text-sm text-muted-foreground">
          Read-only access. An administrator can update this tracker.
        </p>
      )}
      {visible.length === 0 && (
        <p className="rounded-lg border p-6 text-muted-foreground">No tasks match these filters.</p>
      )}
      {Array.from(new Set(visible.map((task) => task.category))).map((category) => (
        <section key={category} className="space-y-3">
          <h2 className="text-lg font-semibold">{category}</h2>
          {visible
            .filter((task) => task.category === category)
            .map((task) => (
              <Card key={task.id}>
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">{task.title}</CardTitle>
                      <p className="mt-1 text-sm text-muted-foreground">{task.description}</p>
                    </div>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-medium ${task.status === "verified" ? "bg-emerald-500/10 text-emerald-700" : task.status === "blocked" ? "bg-amber-500/10 text-amber-700" : "bg-muted text-muted-foreground"}`}
                    >
                      {statuses[task.status]}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <p className={overdue(task) ? "text-destructive" : "text-muted-foreground"}>
                    Owner: {task.owner || "Unassigned"} · Due: {task.due_date || "Not set"}
                    {overdue(task) ? " · Overdue" : ""}
                  </p>
                  {task.blocker && (
                    <p className="whitespace-pre-wrap break-words">
                      <strong>Dependency: </strong>
                      {task.blocker}
                    </p>
                  )}
                  {task.evidence && (
                    <p className="whitespace-pre-wrap break-words">
                      <strong>Evidence: </strong>
                      {task.evidence}
                    </p>
                  )}
                  {task.notes && (
                    <p className="whitespace-pre-wrap break-words">
                      <strong>Notes: </strong>
                      {task.notes}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {task.updated_at
                      ? `Updated ${new Date(task.updated_at).toLocaleString()} · Staff ${task.updated_by}`
                      : "Baseline review · 6 October 2026"}
                  </p>
                  {canEdit &&
                    (editing === task.id ? (
                      <TaskForm
                        key={task.id}
                        task={task}
                        onClose={() => setEditing(null)}
                        onSaved={() => {
                          setEditing(null);
                          setSaved(`Saved: ${task.title}`);
                        }}
                      />
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditing(task.id);
                          setSaved("");
                        }}
                      >
                        Update task
                      </Button>
                    ))}
                </CardContent>
              </Card>
            ))}
        </section>
      ))}
    </div>
  );
}

function TaskForm({
  task,
  onClose,
  onSaved,
}: {
  task: ReadinessTask;
  onClose: () => void;
  onSaved: () => void;
}) {
  const client = useQueryClient();
  const [revision] = useState(task.revision);
  const [form, setForm] = useState({
    status: task.status,
    owner: task.owner,
    due_date: task.due_date ?? "",
    blocker: task.blocker,
    evidence: task.evidence,
    notes: task.notes,
  });
  const mutation = useMutation({
    mutationFn: () =>
      rawApi.put<ReadinessTask>(`${endpoint}/${task.id}`, {
        ...form,
        due_date: form.due_date || null,
        revision,
      }),
    onSuccess: (updated) => {
      client.setQueryData<ReadinessTask[]>(queryKey, (old) =>
        old?.map((item) => (item.id === updated.id ? updated : item)),
      );
      onSaved();
    },
  });
  const field = (name: "owner" | "due_date" | "blocker" | "evidence" | "notes") => ({
    value: form[name],
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [name]: event.target.value }),
  });
  return (
    <form
      className="space-y-3 border-t pt-4"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      <fieldset disabled={mutation.isPending} className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <SelectField
            label="Task status"
            value={form.status}
            onChange={(event) => setForm({ ...form, status: event.target.value as Status })}
          >
            {Object.entries(statuses).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectField>
          <TextField label="Owner" maxLength={160} {...field("owner")} />
          <TextField label="Due date" type="date" {...field("due_date")} />
        </div>
        <TextAreaField
          label="Dependency or blocker"
          required={form.status === "blocked"}
          maxLength={3000}
          {...field("blocker")}
        />
        <TextAreaField
          label="Evidence"
          hint="Required for Verified. Record a test result, review reference or receipt location. Keep API secrets out of this tracker."
          required={form.status === "verified"}
          maxLength={6000}
          {...field("evidence")}
        />
        <TextAreaField label="Notes and next action" maxLength={6000} {...field("notes")} />
        <div className="flex gap-2">
          <Button type="submit">{mutation.isPending ? "Saving…" : "Save task"}</Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </fieldset>
      {mutation.isError && (
        <div role="alert">
          <p className="text-destructive">
            {mutation.error instanceof ApiError && mutation.error.status === 409
              ? "This task changed. Reload the tracker before saving again."
              : errorMessage(mutation.error)}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={async () => {
              await client.invalidateQueries({ queryKey });
              onClose();
            }}
          >
            Reload saved tasks
          </Button>
        </div>
      )}
    </form>
  );
}
